import { useEffect, useState } from 'react';
import { submitCurrentGradeItem } from 'utils/gradeActions';
import { DISPOSITION_COLORS, PRODUCT_CONDITION_TYPES } from 'utils/constants';
import { useProductStore, useReceiptStore } from 'store';
import { Button } from 'components/ui/Button';
import { CardTitle } from 'components/ui/card';
import { cn } from 'utils/classnames';

export default function GradeItem() {
  const [loading, setLoading] = useState<boolean>(false);
  const {
    currentProduct: productLine,
    isProductNew,
    setProductNew,
    productCondition,
    setProductCondition,
    isRejected,
    setIsRejected,
    toggleIsRejected,
  } = useProductStore();
  const { receiptData } = useReceiptStore();

  const handleSubmit = async (): Promise<void> => {
    setLoading(true);
    const result = await submitCurrentGradeItem(isRejected);

    if (!result.success) {
      setLoading(false);
    }
  };

  const isUnexpected = productLine?.expected_ind === 'n';
  const isCallback =
    productLine?.exception_process === 'callback' || productLine?.disposition_stage === 'callback';

  const colors = isCallback ? DISPOSITION_COLORS.CALLBACK : DISPOSITION_COLORS.GRADING;

  const activeBlue = isCallback
    ? 'bg-orange-600 text-white hover:bg-orange-700'
    : 'bg-blue-600 text-white hover:bg-blue-700';

  const optionButtonClass = (active: boolean, tone: 'primary' | 'yellow') =>
    cn(
      'h-10 flex-1 rounded-xl border-0 text-sm font-bold shadow-sm',
      active
        ? tone === 'primary'
          ? activeBlue
          : 'bg-amber-400 text-black hover:bg-amber-500'
        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
    );

  useEffect(() => {
    setProductCondition(null);
    setProductNew(null);
    setIsRejected(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={cn('-mx-4 -mb-6 flex min-h-full flex-col gap-3 px-4 pb-6 pt-4', colors.gradient)}
    >
      <div className="flex items-center justify-center gap-3">
        <CardTitle className="text-lg font-extrabold tracking-tight text-white">
          Grade Item
        </CardTitle>
        {isUnexpected && (
          <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-bold text-red-600">
            UNEXPECTED
          </span>
        )}
        {isCallback && (
          <span className="rounded-full border border-white/40 bg-white/20 px-2.5 py-0.5 text-xs font-bold text-white">
            CALLBACK
          </span>
        )}
      </div>

      {productLine?.style_image_url && (
        <div className="my-0.5 flex min-h-[200px] max-h-[250px] items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-3 text-lg font-semibold text-slate-400 shadow-sm">
          <img
            src={productLine.style_image_url}
            alt="Product"
            className="max-h-[250px] max-w-full object-contain"
          />
        </div>
      )}

      <div
        className={cn(
          'rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition-opacity',
          isRejected && 'opacity-40'
        )}
      >
        <div className="mb-2 text-xs font-bold text-slate-700">Is New?</div>
        <div className="flex gap-2">
          <Button
            type="button"
            onClick={() => {
              setIsRejected(false);
              setProductNew(true);
            }}
            disabled={loading}
            className={optionButtonClass(isProductNew === true, 'primary')}
          >
            Yes
          </Button>
          <Button
            type="button"
            onClick={() => {
              setIsRejected(false);
              setProductNew(false);
            }}
            disabled={loading}
            className={optionButtonClass(isProductNew === false, 'yellow')}
          >
            No
          </Button>
        </div>

        {isProductNew === true && (
          <div className="mt-3">
            <div className="mb-2 text-xs font-bold text-slate-700">Has Packaging?</div>
            <div className="flex gap-2">
              <Button
                type="button"
                onClick={() => {
                  setIsRejected(false);
                  setProductCondition(PRODUCT_CONDITION_TYPES.HAS_PACKAGING);
                }}
                disabled={loading}
                className={optionButtonClass(
                  productCondition === PRODUCT_CONDITION_TYPES.HAS_PACKAGING,
                  'primary'
                )}
              >
                Yes
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setIsRejected(false);
                  setProductCondition(PRODUCT_CONDITION_TYPES.NO_PACKAGING);
                }}
                disabled={loading}
                className={optionButtonClass(
                  productCondition === PRODUCT_CONDITION_TYPES.NO_PACKAGING,
                  'yellow'
                )}
              >
                No
              </Button>
            </div>
          </div>
        )}

        {isProductNew === false && (
          <div className="mt-3">
            <div className="mb-2 text-xs font-bold text-slate-700">Condition?</div>
            <div className="flex gap-2">
              <Button
                type="button"
                onClick={() => {
                  setIsRejected(false);
                  setProductCondition(PRODUCT_CONDITION_TYPES.USABLE);
                }}
                disabled={loading}
                className={optionButtonClass(
                  productCondition === PRODUCT_CONDITION_TYPES.USABLE,
                  'primary'
                )}
              >
                Usable
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setIsRejected(false);
                  setProductCondition(PRODUCT_CONDITION_TYPES.UNUSABLE);
                }}
                disabled={loading}
                className={optionButtonClass(
                  productCondition === PRODUCT_CONDITION_TYPES.UNUSABLE,
                  'yellow'
                )}
              >
                Unusable
              </Button>
            </div>
          </div>
        )}
      </div>

      <div
        className={cn(
          'rounded-xl border p-3 shadow-sm transition-colors',
          isRejected ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white'
        )}
      >
        <div
          className={cn(
            'mb-2 text-xs font-bold transition-colors',
            isRejected ? 'text-red-700' : 'text-slate-700'
          )}
        >
          Reject?
        </div>
        <Button
          type="button"
          onClick={toggleIsRejected}
          disabled={loading}
          className={cn(
            'h-10 w-full rounded-xl border-0 text-sm font-bold shadow-sm',
            isRejected
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          )}
        >
          Reject
        </Button>
      </div>

      <Button
        type="button"
        onClick={handleSubmit}
        disabled={loading || (!isRejected && (isProductNew === null || productCondition === null))}
        className="h-11 w-full bg-emerald-500 px-7 text-sm font-bold text-white shadow-[0_4px_12px_rgba(40,167,69,0.35)] hover:bg-emerald-600 disabled:opacity-40"
      >
        {loading ? 'Submitting...' : 'Submit'}
      </Button>

      <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
          <span className="text-slate-500">
            Tracking:{' '}
            <span className="font-semibold text-slate-900 break-all">
              {receiptData?.tracking_id || receiptData?.receipt?.tracking_id}
            </span>
          </span>
          <span className="text-slate-500">
            Article #:{' '}
            <span className="font-semibold text-slate-900">
              {productLine?.product_id === 'Unknown' ? 'N/A' : productLine?.product_id}
            </span>
          </span>
          <span className="text-slate-500">
            Desc:{' '}
            <span className="font-semibold text-slate-900">
              {productLine?.product_id === 'Unknown' ? 'N/A' : productLine?.description || 'N/A'}
            </span>
          </span>
          <span className="text-slate-500">
            Color/Size:{' '}
            <span className="font-semibold text-slate-900">
              {productLine?.product_id === 'Unknown'
                ? 'N/A'
                : (productLine?.style_color || 'N/A') +
                  ' - ' +
                  (productLine?.product_size || 'N/A')}{' '}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}
